import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { isDefined, mapAsKeys } from '@sama/js-core';
import { isEmpty, pick } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isUndefined } from 'lodash';
import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { keys } from '../../../cross-team-shared/misc-utils/keys';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { Submission } from '../../../team-apis/otterdam/submission';
import { Task, TaskState } from '../../../team-apis/otterdam/task';
import { Selection } from '../../h3ml/state/fusion-state';
import { OutputInfoByName } from '../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AnnotationLoadingService } from '../../http/loading/annotation-loading.service';
import { PRE_ANNOTATIONS_SUBMISSION_ID } from '../../http/loading/submission-loading.service';
import { TaskLoadingService } from '../../http/loading/task-loading.service';
import { ObjectSelectionService } from '../../outputs/annotation/annotation-shared/utils/object-selection.service';
import { ShapeService } from '../../shared/misc-services/shape.service';
import { SubmissionDropdownItem } from '../navbar/submissions-navigation/submissions-navigation.component';

@Component({
  selector: 'h3-compare-answers',
  templateUrl: './compare-answers.component.html',
  styleUrls: ['./compare-answers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CompareAnswersComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input({ required: true }) submissionItems$!: Observable<
    SubmissionDropdownItem[]
  >;
  @Input() submissionId?: number;
  @Output() closeOverlay = new EventEmitter<void>();
  @Output() isLoading = new EventEmitter<boolean>();

  defaultAnswersId = PRE_ANNOTATIONS_SUBMISSION_ID;
  hasDefaultAnswers$: BehaviorSubject<boolean>;
  hasTrainingAnswers: boolean;

  private initialState: {
    task: Task;
    outputInfoByName: OutputInfoByName;
  };
  private shapeService = inject(ShapeService);
  private objectSelectionService = inject(ObjectSelectionService);
  private switchAnswersTrigger$ = new Subject<number>();

  constructor(
    private eventTrackingService: EventTrackingService,
    private store: TaskWorkspaceStore,
    private taskLoadingService: TaskLoadingService,
    private annotationLoadingService: AnnotationLoadingService,
    injector: Injector,
  ) {
    super(injector);
    this.subscribeToSwitchAnswersTriggers();
    this.initialState = pick(this.store.state(), 'task', 'outputInfoByName');

    // this doesn't cover raster default answer, but we chose not to support it
    // for now
    const { task } = this.store.state();
    this.hasDefaultAnswers$ = new BehaviorSubject(
      !isEmpty(task.default_answers),
    );
    this.hasTrainingAnswers = this.shouldDisplayTrainingAnswers();
  }

  async ngOnInit() {
    if (this.store.isUsingObjects()) {
      const { task } = this.store.state();
      const externalAnswers =
        await this.annotationLoadingService.loadPreAnnotations(task.id);
      this.hasDefaultAnswers$.next(!isEmpty(externalAnswers));
    }
  }

  ngAfterViewInit() {
    this.isLoading.emit(false);
  }

  ngOnDestroy() {
    // completing the subject just to avoid having lingering subscriptions
    this.closeOverlay.complete();
  }

  switchAnswer(submissionId?: number) {
    if (!submissionId) {
      return;
    }
    this.switchAnswersTrigger$.next(submissionId);
  }

  async loadTrainingTask() {
    await this.annotationLoadingService.loadAnnotationsForTraining();
  }

  resetAnswerAndCloseOverlay() {
    this.setAnswers(this.initialState.task, this.initialState.outputInfoByName);
    this.closeOverlay.emit();
  }

  private subscribeToSwitchAnswersTriggers() {
    this.subscribeTo(
      this.switchAnswersTrigger$.pipe(
        // This will ensure that requests aren't queued and in-flight requests
        // are ignored by newer requests
        switchMap((submissionId) => {
          this.isLoading.emit(true);
          return from(this.loadTask(submissionId));
        }),
      ),
      ({ task, submissionId }) => {
        this.setAnswers(task, this.store.state().outputInfoByName);
        this.eventTrackingService.track({
          id: 'change_displayed_answer',
          bucket: 'untracked',
          submission_id:
            submissionId === this.defaultAnswersId ? null : submissionId,
        });
        this.isLoading.emit(false);
      },
    );
  }

  private async loadTask(submissionId: number) {
    let submission: Submission | undefined;
    let task: Task;

    if (submissionId === PRE_ANNOTATIONS_SUBMISSION_ID) {
      task = {
        ...this.initialState.task,
        answers_or_gold: this.initialState.task.default_answers,
      };
    } else {
      task = await this.taskLoadingService.loadForReadOnly(submissionId);
      submission = task.submissions?.find((s) => s.id === submissionId);
    }

    if (this.store.isUsingObjects()) {
      const currentShapeSelection = this.store.state().shapeSelection;
      const currentObjectSelection = this.store.state().objectSelection;
      await this.annotationLoadingService.loadSubmissionAnswers(
        task,
        submission,
      );
      this.updateSelection(currentShapeSelection, currentObjectSelection);
    }

    return { task, submissionId };
  }

  private updateSelection(
    shapeSelection: Selection,
    objectSelection: Selection,
  ) {
    const validShapeIds = keys(shapeSelection).filter((id) =>
      this.shapeService.shapeExists(id),
    );
    const newShapeSelection: Selection = mapAsKeys(validShapeIds, () => true);
    const validObjectIds = keys(objectSelection).filter((value) =>
      isDefined(this.store.state().objects[value]),
    );

    this.objectSelectionService.setSelection(validObjectIds);
    this.store('shapeSelection').set(newShapeSelection);
  }

  private setAnswers(task: Task, outputInfoByName: OutputInfoByName) {
    this.store.resetOutputInfoAndExtras(task, outputInfoByName);
    this.store.compareAnswerChanged$.next();
  }

  private shouldDisplayTrainingAnswers() {
    const { project, task } = this.store.state();
    if (
      !this.store.isUsingObjects() ||
      project.project_type !== 'Certification'
    ) {
      return false;
    }
    // Did not include 'rejected' because training tasks can't have that state
    const invalidTaskStates: TaskState[] = ['new', 'in progress'];
    if (isUndefined(task.state) || invalidTaskStates.includes(task.state)) {
      return false;
    }
    return true;
  }
}
